import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { LeagueShowWrapper } from '@seedlang/hoc';
import { AppUI } from '@seedlang/state';
import { flexCenterColumn } from '@seedlang/style_mixins';
import { isBlank, isPresent } from '@seedlang/utils';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import Spinner from 'components/spinner';
import { observer } from 'mobx-react';
import React from 'react';
import LeagueBadgeIcon from './league_badge_icon';
import LeagueHistory from './league_history';
import LeagueTable from './league_table';

const Wrapper = styled.div`
  ${flexCenterColumn()}
  width: 100%;
  padding: 20px;
`;

const LeagueTitle = styled.h2`
  margin: 0;
  text-align: center;
`;

const Note = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: ${props => props.margin};
  font-size: 14px;
  font-color: #333;
  font-weight: bold;
  color: ${props => props.color || Theme.darkBlue};
`;

const HistoryTitle = styled.div`
  display: block;
  width: 100%;
  text-align: center;
  font-size: 20px;
  line-height: 20px;
  font-weight: bold;
  margin: 30px 0 20px 0;
`;

const LeagueBadges = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  svg {
    width: 80px;
    height: 80px;
    @media (max-width: 500px) {
      width: 50px;
      height: 50px;
    }
  }
  #current-badge svg {
    width: 105px;
    height: 105px;
    @media (max-width: 500px) {
      width: 60px;
      height: 60px;
    }
  }
`;

const NotesWrapper = styled.div`
  margin: 25px 0 20px 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
`;

@observer
class LeagueShow extends React.Component {

  render() {
    if (isBlank(this.props.league)) {
      return (
        <Wrapper>
          <Spinner />
        </Wrapper>
      );
    }
    return (
      <Wrapper>
        <LeagueTitle>{this.props.league.leagueLevel?.name}</LeagueTitle>
        <LeagueBadges>
          {[1, 2, 3, 4, 5, 6].map(num => (
            <LeagueBadgeIcon
              key={num}
              leagueLevelPosition={num <= this.props.league.leagueLevel?.position ? num : 0}
              width={num === this.props.league.leagueLevel?.position ? "95px" : "70px"}
              margin="0 5px"
            />
          ))}
        </LeagueBadges>
        <NotesWrapper>
          {
            this.props.league.ended && !this.props.userIsInLeague &&
            <Note>
              This league has ended.
            </Note>
          }
          {
            this.props.user && this.props.league.ended && this.props.userIsInLeague &&
            <>
              <Note color="#000">
                {
                  this.props.league.ended &&
                  `${this.props.userIsAuthUser ? 'You' : this.props.user?.name} finished #${this.props.userLeague?.rank},`
                }
                {
                  this.props.userLeague?.promoted ?
                    ` and ${this.props.userIsAuthUser ? 'were' : 'was'} promoted to the next league.`
                    : this.props.userLeague?.demoted ?
                      ` and ${this.props.userIsAuthUser ? 'were' : 'was'} demoted to the previous league.`
                      :
                      ` and will stay in the ${this.props.league.leagueLevel?.name}.`
                }
              </Note>
              {
                this.props.userIsAuthUser && this.props.isUsersLastLeague &&
                <Note>
                  Earn XP to join your new league.
                </Note>
              }
            </>
          }
          {
            !this.props.league.ended &&
            <>
              {
                this.props.user && this.props.userIsInLeague &&
                <Note color="#000">
                  {this.props.statusText}
                </Note>
              }
              <Note>
                {this.props.leagueEndedText}
              </Note>
            </>
          }
          {
            this.props.isFirstLeagueMember &&
            <Note
              color={Theme.darkRed}
            >
              {this.props.firstLeagueMemberText}
            </Note>
          }
        </NotesWrapper>
        <LeagueTable
          league={this.props.league}
          userId={this.props.userId}
        />
        {
          isPresent(this.props.user) &&
          <>
            <HistoryTitle>{this.props.userIsAuthUser ? 'Your League History' : `${this.props.user?.name}'s League History`}</HistoryTitle>
            <LeagueHistory
              hideActiveLeague={this.props.league?.active}
              padding='0 10px'
              user={this.props.user}
              selectedLeague={this.props.league}
            />
          </>
        }
      </Wrapper>
    );
  }
}

export default ErrorBoundary(LeagueShowWrapper(LeagueShow));
